import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { marked } from 'marked';
import { ArrowLeft, ArrowRight, Calendar, Clock, RefreshCw, User } from 'lucide-react';
import { api } from '../lib/api';
import SEO from '../components/SEO';
import { getPillarForPost } from '../data/pillarsData';

marked.setOptions({ gfm: true, breaks: true, headerIds: true, mangle: false });

const formatArticleDate = (iso, locale = 'en-US') => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
};

const POST_COPY = {
  en: {
    backLabel: 'Articles',
    articleBase: '/articles',
    minRead: 'min read',
    publishedOn: 'Published',
    updatedOn: 'Updated',
    keyTakeawaysHeading: 'Key Takeaways',
    faqHeading: 'Frequently Asked Questions',
    relatedHeading: 'Related Reading',
    langSwitchLabel: 'Leer en Espa\u00f1ol',
    locale: 'en-US',
  },
  es: {
    backLabel: 'Art\u00edculos',
    articleBase: '/es/articles',
    minRead: 'min de lectura',
    publishedOn: 'Publicado',
    updatedOn: 'Actualizado',
    keyTakeawaysHeading: 'Puntos clave',
    faqHeading: 'Preguntas frecuentes',
    relatedHeading: 'Lecturas relacionadas',
    langSwitchLabel: 'Read in English',
    locale: 'es-ES',
  },
};

const BlogPostPage = ({ lang = 'en' }) => {
  const t = POST_COPY[lang] || POST_COPY.en;
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(null);
  const [translationPeer, setTranslationPeer] = useState(null);

  useEffect(() => {
    setPost(null);
    setRelated([]);
    setError(null);
    setTranslationPeer(null);
    api(`/api/blog/${slug}`)
      .then(setPost)
      .catch((e) => setError(e.message));
  }, [slug]);

  // Once the current post resolves, pull related posts (same pillar, fall back
  // to most-recent overall to always have 3 internal links for crawlers).
  useEffect(() => {
    if (!post) return;
    api(`/api/blog?lang=${lang}`)
      .then((all) => {
        if (!Array.isArray(all)) { setRelated([]); return; }
        const others = all.filter((p) => p.slug !== post.slug);
        const samePillar = post.pillar ? others.filter((p) => p.pillar === post.pillar) : [];
        const remaining = others.filter((p) => !samePillar.includes(p));
        setRelated([...samePillar, ...remaining].slice(0, 3));
      })
      .catch(() => setRelated([]));
  }, [post, lang]);

  // Look up the sibling-language post so we can render an hreflang alternate
  // + a small language-switch link in the header.
  useEffect(() => {
    if (!post) return;
    const otherLang = lang === 'es' ? 'en' : 'es';
    api(`/api/blog?lang=${otherLang}`)
      .then((all) => {
        if (!Array.isArray(all)) return;
        const match = all.find(
          (p) => p.translation_of === post.id || p.id === post.translation_of
        );
        setTranslationPeer(match || null);
      })
      .catch(() => setTranslationPeer(null));
  }, [post, lang]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-stone-900 mb-3">Post not found</h1>
          <p className="text-stone-500 mb-6">{error}</p>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-rose-500 hover:text-rose-600 font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-400">Loading...</div>
    );
  }

  const bodyHtml = marked.parse(post.body_markdown || '');

  // Only http(s) URLs are valid for Schema.org image / og:image — data: URIs
  // (from base64-uploaded/generated covers) must be excluded or Google flags
  // them as "Incorrect value type" in Search Console.
  const coverIsHttp =
    typeof post.cover_image === 'string' && /^https?:\/\//i.test(post.cover_image);

  // Build all the schemas for this article
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    headline: post.title,
    description: post.excerpt || post.title,
    image: coverIsHttp ? [post.cover_image] : undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    inLanguage: lang === 'es' ? 'es' : 'en-US',
    author: {
      '@type': 'Person',
      name: post.author_name || 'Akira Signature Care',
      jobTitle: post.author_credentials,
      affiliation: { '@id': 'https://akirasignaturecare.com/#clinic' },
    },
    publisher: { '@id': 'https://akirasignaturecare.com/#clinic' },
    mainEntityOfPage: `https://akirasignaturecare.com${t.articleBase}/${post.slug}`,
    isPartOf: { '@id': 'https://akirasignaturecare.com/#website' },
    about: post.pillar ? { '@type': 'Thing', name: post.pillar } : undefined,
  };
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://akirasignaturecare.com/' },
      { '@type': 'ListItem', position: 2, name: t.backLabel, item: `https://akirasignaturecare.com${t.articleBase}` },
      { '@type': 'ListItem', position: 3, name: post.title, item: `https://akirasignaturecare.com${t.articleBase}/${post.slug}` },
    ],
  };
  const faqJsonLd = post.faqs && post.faqs.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  const schemas = [articleSchema, breadcrumb];
  if (faqJsonLd) schemas.push(faqJsonLd);

  const peerLang = lang === 'es' ? 'en' : 'es';
  const peerBase = peerLang === 'es' ? '/es/articles' : '/articles';
  const alternates = translationPeer
    ? [{ hreflang: peerLang, href: `${peerBase}/${translationPeer.slug}` }]
    : [];

  return (
    <div className="min-h-screen bg-stone-50" data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={post.title}
        description={post.excerpt || `${post.title} — physician-written article from Akira Signature Care.`}
        path={`${t.articleBase}/${post.slug}`}
        image={coverIsHttp ? post.cover_image : undefined}
        type="article"
        schema={schemas}
        lang={lang}
        alternates={alternates}
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 text-white pt-24 pb-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <Link
            to={t.articleBase}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> {t.backLabel}
          </Link>

          {post.pillar && (() => {
            const pillarConfig = getPillarForPost(post.pillar);
            return pillarConfig && lang === 'en' ? (
              <Link
                to={`/articles/pillar/${pillarConfig.slug}`}
                data-testid="article-pillar-link"
                className="inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 mb-5 transition-colors"
              >
                {post.pillar}
              </Link>
            ) : (
              <div className="inline-block bg-white/10 backdrop-blur-sm text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 mb-5">
                {post.pillar}
              </div>
            );
          })()}

          <h1
            className="font-serif text-4xl lg:text-5xl mb-6 leading-tight"
            style={{ textShadow: '0 2px 24px rgba(0,0,0,0.45)' }}
          >
            {post.title}
          </h1>

          {translationPeer && (
            <Link
              to={`${peerBase}/${translationPeer.slug}`}
              data-testid="article-lang-switch"
              className="inline-flex items-center gap-2 mb-6 text-rose-300 hover:text-white text-xs tracking-[0.3em] uppercase border-b border-rose-300/40 hover:border-white pb-1 transition-colors"
            >
              {t.langSwitchLabel}
              <ArrowRight className="w-3 h-3" />
            </Link>
          )}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-stone-300 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-rose-300" />
              <div>
                <div className="text-white font-medium">{post.author_name}</div>
                {post.author_credentials && (
                  <div className="text-xs text-stone-300 font-light">{post.author_credentials}</div>
                )}
              </div>
            </div>
            {post.reading_time_min ? (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-rose-300" />
                {post.reading_time_min} {t.minRead}
              </div>
            ) : null}
            {post.created_at && (
              <div className="flex items-center gap-2" data-testid="article-published-date">
                <Calendar className="w-4 h-4 text-rose-300" />
                <time dateTime={new Date(post.created_at).toISOString()}>
                  {t.publishedOn} {formatArticleDate(post.created_at, t.locale)}
                </time>
              </div>
            )}
            {post.updated_at && post.created_at && Math.abs(new Date(post.updated_at) - new Date(post.created_at)) > 24 * 60 * 60 * 1000 && (
              <div className="flex items-center gap-2 text-stone-300" data-testid="article-updated-date">
                <RefreshCw className="w-4 h-4 text-rose-300" />
                <time dateTime={new Date(post.updated_at).toISOString()}>
                  {t.updatedOn} {formatArticleDate(post.updated_at, t.locale)}
                </time>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16">
        <div className="container mx-auto px-6 max-w-3xl">
          {post.key_takeaways && post.key_takeaways.length > 0 && (
            <div className="bg-rose-50 border-l-4 border-rose-400 p-6 rounded-r-2xl mb-12">
              <div className="text-[11px] tracking-[0.3em] uppercase text-rose-500 font-semibold mb-3">
                {t.keyTakeawaysHeading}
              </div>
              <ul className="space-y-2 text-stone-800 text-base leading-relaxed">
                {post.key_takeaways.map((takeaway, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-rose-400 mt-1">•</span>
                    <span>{takeaway}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <article
            className="prose prose-stone prose-lg max-w-none
                       prose-headings:font-serif prose-headings:text-stone-900
                       prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
                       prose-h3:text-xl prose-h3:mt-8
                       prose-p:text-stone-700 prose-p:leading-relaxed
                       prose-a:text-rose-500 prose-a:no-underline hover:prose-a:underline
                       prose-strong:text-stone-900
                       prose-li:text-stone-700"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />

          {post.faqs && post.faqs.length > 0 && (
            <div className="mt-16">
              <h2 className="font-serif text-3xl text-stone-900 mb-6">
                {t.faqHeading}
              </h2>
              <div className="space-y-4">
                {post.faqs.map((f, i) => (
                  <div key={i} className="bg-white border border-stone-100 p-6 rounded-2xl">
                    <h3 className="font-semibold text-stone-900 mb-2">{f.q}</h3>
                    <p className="text-stone-700 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {related.length > 0 && (
            <section className="mt-20" data-testid="related-articles-section">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-px bg-rose-400" />
                <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
                  {t.relatedHeading}
                </span>
              </div>
              <h2
                className="font-serif text-3xl text-stone-900 mb-8 leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {lang === 'es' ? 'Sigue explorando' : 'Keep exploring'}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`${t.articleBase}/${r.slug}`}
                    data-testid={`related-article-${r.slug}`}
                    className="group block bg-white border border-stone-200 hover:border-rose-300 hover:-translate-y-1.5 hover:shadow-xl transition-all duration-500 ease-out shadow-sm overflow-hidden"
                  >
                    {r.cover_image ? (
                      <div className="h-40 overflow-hidden">
                        <img
                          src={r.cover_image}
                          alt={r.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                    ) : (
                      <div className="h-40 bg-gradient-to-br from-stone-100 via-rose-50 to-stone-50" />
                    )}
                    <div className="p-5">
                      {r.pillar && (
                        <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-2 line-clamp-1">
                          {r.pillar}
                        </div>
                      )}
                      <h3
                        className="font-serif text-lg text-stone-900 leading-snug group-hover:text-rose-500 transition-colors line-clamp-3"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {r.title}
                      </h3>
                      {r.reading_time_min ? (
                        <div className="flex items-center gap-1 text-stone-400 text-xs mt-3">
                          <Clock className="w-3 h-3" />
                          {r.reading_time_min} min read
                        </div>
                      ) : null}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="mt-20 bg-white border border-stone-100 p-8 rounded-2xl">
            <div className="text-[10px] tracking-[0.3em] uppercase text-rose-500 mb-2">
              About the author
            </div>
            <div className="font-serif text-2xl text-stone-900 mb-1">{post.author_name}</div>
            {post.author_credentials && (
              <div className="text-stone-500 text-sm font-light mb-4">{post.author_credentials}</div>
            )}
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-rose-500 hover:text-rose-600 font-medium text-sm"
            >
              Meet our team
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
